import { useMemo } from "react";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../store/hooks";
import { evaluate_conditional_logic_for_enduser_fields } from "@tellescope/utilities";
import type { Form, FormResponse } from "@tellescope/types-models";

export interface PinnedFormDisplay {
  form: Form;
  /** Whether there's an outstanding (unsubmitted) form response */
  hasOutstanding: boolean;
  /** The outstanding form response if any */
  outstandingResponse: FormResponse | null;
  /** The most recently submitted form response if any */
  latestSubmitted: FormResponse | null;
}

export interface UseEnduserPinnedFormsResult {
  forms: PinnedFormDisplay[];
  isLoading: boolean;
  error: Error | null;
  /** IDs of unsubmitted form responses that belong to pinned forms (no draft, no submitter).
   *  These should be hidden from the form responses list since the pinned forms block
   *  will reuse them when the enduser clicks the form again. */
  pinnedOutstandingResponseIds: string[];
}

/**
 * Hook for fetching forms available for portal submission.
 *
 * Fetches:
 * 1. All forms with allowPortalSubmission: true
 * 2. All form responses — filtered client-side to match pinned forms
 *
 * Both queries are reactive (cache-subscribed) so pinnedOutstandingResponseIds
 * updates in the same render cycle as the form responses list, preventing
 * abandoned responses from flashing in the responses block.
 *
 * Returns forms sorted by title.
 */
export const useEnduserPinnedForms = (): UseEnduserPinnedFormsResult => {
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);

  const formsQuery = useTellescopeInfiniteQuery({
    model: "forms",
    method: "getSome",
    request: { filter: { allowPortalSubmission: true } },
    select: (data) => data.pages.flat(),
  });

  const responsesQuery = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: { filter: {} },
    select: (data) => data.pages.flat(),
  });

  const isLoading = formsQuery.isLoading || responsesQuery.isLoading;
  const error = formsQuery.error ?? responsesQuery.error ?? null;

  const { forms, pinnedOutstandingResponseIds } = useMemo(() => {
    const allForms = formsQuery.data ?? [];
    const allResponses = responsesQuery.data ?? [];
    const enduserId = currentEnduser?.id;

    // Filter forms based on enduser condition if specified
    const eligibleForms = currentEnduser
      ? allForms.filter((form) => {
          // If no condition is specified, include the form
          if (!form.allowPortalSubmissionEnduserCondition) return true;
          // Evaluate the condition against the current enduser; exclude on failure
          try {
            return evaluate_conditional_logic_for_enduser_fields(
              currentEnduser,
              form.allowPortalSubmissionEnduserCondition
            );
          } catch {
            return false;
          }
        })
      : allForms;

    // Get form IDs for filtering responses to only those related to pinned forms
    const formIds = eligibleForms.map((f) => f.id);

    // Filter all fetched responses down to those visible for this enduser
    const visibleResponses = allResponses.filter((f) => {
      // Not manually hidden from enduser portal
      if (f.hideFromEnduserPortal) return false;
      // Not an unsubmitted internal note
      if (!f.submittedAt && !!f.isInternalNote) return false;
      // Must be related to one of our pinned forms
      if (!formIds.includes(f.formId)) return false;
      // Must be submitted by this enduser or still outstanding (includes draft-saved)
      const submittedByEnduser = f.submittedBy === enduserId;
      const isOutstanding = !f.submittedBy;
      return submittedByEnduser || isOutstanding;
    });

    // Accumulates IDs of pure-pending responses across all forms to suppress in the responses list
    const allPinnedOutstandingIds: string[] = [];

    const displayForms = eligibleForms
      .map((form) => {
        const responses = visibleResponses.filter((r) => r.formId === form.id);

        // Pure-pending: responses with no progress that were created incidentally by the enduser
        // clicking the pinned form (not intentionally sent by staff or an automation). These are
        // suppressed from the form responses list and reused on the next pinned form click.
        // A response is NOT suppressed if any of the following indicate intentional delivery:
        //   - sharedVia: sent to the enduser via Email, SMS, or Chat
        //   - automationStepId: created by a care plan or automation journey
        //   - calendarEventId: linked to an appointment (e.g. pre-visit intake)
        //   - carePlanId: linked to a care plan
        //   - pushedToPortalAt: explicitly pushed to the portal by a staff member
        // Once the enduser answers at least one field (responses.length > 0), the response also
        // graduates out of this set and becomes visible regardless of origin.
        const purePendingResponses = responses
          .filter(
            (r) =>
              !r.submittedAt &&
              !r.submittedBy &&
              !r.draftSavedAt &&
              !r.responses?.length &&
              !r.sharedVia &&
              !r.automationStepId &&
              !r.calendarEventId &&
              !r.carePlanId &&
              !r.pushedToPortalAt
          )
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        allPinnedOutstandingIds.push(...purePendingResponses.map((r) => r.id));

        // Use the newest pure-pending response when the enduser clicks the form again.
        // Fall back to any unsubmitted response (e.g. one with a draft save) if no pure-pending exists.
        const outstandingResponse = purePendingResponses[0] ?? responses.find((r) => !r.submittedAt) ?? null;

        const submittedResponses = responses
          .filter((r) => !!r.submittedAt)
          .sort((a, b) => new Date(b.submittedAt!).getTime() - new Date(a.submittedAt!).getTime());
        const latestSubmitted = submittedResponses[0] ?? null;

        return {
          form,
          hasOutstanding: !!outstandingResponse,
          outstandingResponse,
          latestSubmitted,
        } satisfies PinnedFormDisplay;
      })
      // Sort forms alphabetically by display title
      .sort((a, b) => {
        const titleA = a.form.displayTitle || a.form.title || "";
        const titleB = b.form.displayTitle || b.form.title || "";
        return titleA.localeCompare(titleB);
      });

    return { forms: displayForms, pinnedOutstandingResponseIds: allPinnedOutstandingIds };
  }, [formsQuery.data, responsesQuery.data, currentEnduser]);

  return { forms, isLoading, error, pinnedOutstandingResponseIds };
};
